import { Theme } from "./types";

export const defaultTheme: Theme = {
  name: "Default",
  id: "default",
  description: "Professional dark theme for optimal data visualization",
  isDark: true,

  colors: {
    // Base colors - maintaining current dark theme
    primary: "#60a5fa", // Blue-400
    primaryHover: "#3b82f6", // Blue-500
    primaryForeground: "#ffffff",

    secondary: "#94a3b8", // Slate-400
    secondaryHover: "#64748b", // Slate-500
    secondaryForeground: "#ffffff",

    accent: "#a78bfa", // Violet-400
    accentHover: "#8b5cf6", // Violet-500
    accentForeground: "#ffffff",

    // Semantic colors
    success: "#4ade80", // Green-400
    successHover: "#22c55e", // Green-500
    successForeground: "#ffffff",

    warning: "#fbbf24", // Amber-400
    warningHover: "#f59e0b", // Amber-500
    warningForeground: "#000000",

    danger: "#f87171", // Red-400
    dangerHover: "#ef4444", // Red-500
    dangerForeground: "#ffffff",

    info: "#38bdf8", // Sky-400
    infoHover: "#0ea5e9", // Sky-500
    infoForeground: "#ffffff",

    // Surface colors - based on current gray-900 background
    background: "#111827", // Gray-900
    backgroundSecondary: "#1f2937", // Gray-800
    backgroundTertiary: "#374151", // Gray-700

    surface: "#1f2937", // Gray-800
    surfaceHover: "#374151", // Gray-700
    surfaceBorder: "#4b5563", // Gray-600

    // Text colors
    text: "#f3f4f6", // Gray-100
    textSecondary: "#d1d5db", // Gray-300
    textMuted: "#9ca3af", // Gray-400
    textDisabled: "#6b7280", // Gray-500

    // UI element colors
    border: "#4b5563", // Gray-600
    borderHover: "#6b7280", // Gray-500
    borderFocus: "#60a5fa", // Blue-400

    input: "#374151", // Gray-700
    inputBorder: "#4b5563", // Gray-600
    inputFocus: "#60a5fa", // Blue-400
    inputPlaceholder: "#9ca3af", // Gray-400

    // Chart colors - professional palette
    chart1: "#60a5fa", // Blue
    chart2: "#4ade80", // Green
    chart3: "#a78bfa", // Violet
    chart4: "#fbbf24", // Amber
    chart5: "#f87171", // Red
    chart6: "#38bdf8", // Sky

    // Segment colors (from existing constants)
    segmentGreeting: "#4CAF50",
    segmentPitch: "#2196F3",
    segmentObjection: "#F44336",
    segmentAgreement: "#9C27B0",
    segmentClosing: "#FF9800",

    // Scrollbar colors
    scrollbarTrack: "#374151",
    scrollbarThumb: "#6B7280",
    scrollbarThumbHover: "#9CA3AF",
    scrollbarThumbActive: "#D1D5DB",
    scrollbarCorner: "#374151",
  },

  typography: {
    fontFamilyBase:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    fontFamilyHeading:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    fontFamilyMono:
      'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
    fontFamilyDisplay:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',

    fontSize: {
      xs: "0.75rem",
      sm: "0.875rem",
      base: "1rem",
      lg: "1.125rem",
      xl: "1.25rem",
      "2xl": "1.5rem",
      "3xl": "1.875rem",
      "4xl": "2.25rem",
      "5xl": "3rem",
    },

    fontWeight: {
      thin: 100,
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
      extrabold: 800,
    },

    lineHeight: {
      none: 1,
      tight: 1.25,
      snug: 1.375,
      normal: 1.5,
      relaxed: 1.625,
      loose: 2,
    },

    letterSpacing: {
      tighter: "-0.05em",
      tight: "-0.025em",
      normal: "0em",
      wide: "0.025em",
      wider: "0.05em",
      widest: "0.1em",
    },
  },

  spacing: {
    xs: "0.25rem",
    sm: "0.5rem",
    md: "1rem",
    lg: "1.5rem",
    xl: "2rem",
    "2xl": "3rem",
    "3xl": "4rem",
    "4xl": "6rem",
    "5xl": "8rem",
  },

  borderRadius: {
    none: "0",
    sm: "0.125rem",
    base: "0.25rem",
    md: "0.375rem",
    lg: "0.5rem",
    xl: "0.75rem",
    "2xl": "1rem",
    "3xl": "1.5rem",
    full: "9999px",
  },

  shadows: {
    none: "none",
    sm: "0 1px 2px 0 rgba(0, 0, 0, 0.5)",
    base: "0 1px 3px 0 rgba(0, 0, 0, 0.5), 0 1px 2px 0 rgba(0, 0, 0, 0.3)",
    md: "0 4px 6px -1px rgba(0, 0, 0, 0.5), 0 2px 4px -1px rgba(0, 0, 0, 0.3)",
    lg: "0 10px 15px -3px rgba(0, 0, 0, 0.5), 0 4px 6px -2px rgba(0, 0, 0, 0.3)",
    xl: "0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 10px 10px -5px rgba(0, 0, 0, 0.3)",
    "2xl": "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
    inner: "inset 0 2px 4px 0 rgba(0, 0, 0, 0.3)",
    focus: "0 0 0 3px rgba(96, 165, 250, 0.3)",
    focusPrimary: "0 0 0 3px rgba(96, 165, 250, 0.3)",
    focusSecondary: "0 0 0 3px rgba(148, 163, 184, 0.3)",
    focusDanger: "0 0 0 3px rgba(248, 113, 113, 0.3)",
  },

  animations: {
    duration: {
      instant: "0ms",
      fast: "150ms",
      normal: "300ms",
      slow: "500ms",
      slower: "700ms",
    },

    easing: {
      linear: "linear",
      easeIn: "cubic-bezier(0.4, 0, 1, 1)",
      easeOut: "cubic-bezier(0, 0, 0.2, 1)",
      easeInOut: "cubic-bezier(0.4, 0, 0.2, 1)",
      bounce: "cubic-bezier(0.68, -0.55, 0.265, 1.55)",
      elastic: "cubic-bezier(0.175, 0.885, 0.32, 1.275)",
    },

    animations: {
      fadeIn: "fadeIn",
      fadeOut: "fadeOut",
      slideIn: "slideInFromTop",
      slideOut: "slideOutToTop",
      bounce: "bounce",
      pulse: "pulse",
      spin: "spin",
      wiggle: "wiggle",
    },
  },

  components: {
    button: {
      base: "inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
      sizes: {
        sm: "h-9 px-3",
        md: "h-10 px-4 py-2",
        lg: "h-11 px-8",
      },
      variants: {
        primary: "bg-primary text-primary-foreground hover:bg-primary/90",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        outline:
          "border border-input bg-transparent hover:bg-accent hover:text-accent-foreground",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        danger: "bg-danger text-danger-foreground hover:bg-danger/90",
        success: "bg-success text-success-foreground hover:bg-success/90",
      },
    },

    card: {
      base: "rounded-lg border bg-card text-card-foreground shadow-sm",
      header: "flex flex-col space-y-1.5 p-6",
      body: "p-6 pt-0",
      footer: "flex items-center p-6 pt-0",
    },

    input: {
      base: "flex w-full rounded-md border bg-transparent text-sm file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
      sizes: {
        sm: "h-9 px-3 py-1",
        md: "h-10 px-3 py-2",
        lg: "h-11 px-4 py-2",
      },
      states: {
        default: "border-input",
        focus: "ring-primary ring-offset-background",
        error: "border-danger focus-visible:ring-danger",
        disabled: "opacity-50 cursor-not-allowed",
      },
    },

    badge: {
      base: "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2",
      variants: {
        default: "bg-secondary text-secondary-foreground",
        primary: "bg-primary text-primary-foreground",
        secondary: "bg-secondary text-secondary-foreground",
        success: "bg-success text-success-foreground",
        warning: "bg-warning text-warning-foreground",
        danger: "bg-danger text-danger-foreground",
        info: "bg-info text-info-foreground",
      },
    },

    table: {
      base: "w-full caption-bottom text-sm",
      header: "border-b bg-muted/50",
      row: "border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted",
      cell: "p-4 align-middle [&:has([role=checkbox])]:pr-0",
      striped: "even:bg-muted/30",
      hover: "hover:bg-muted/50",
    },

    modal: {
      overlay: "fixed inset-0 z-50 bg-black/80",
      content: "fixed z-50 gap-4 bg-background p-6 shadow-lg",
      header: "flex flex-col space-y-1.5 text-center sm:text-left",
      body: "flex-1 overflow-y-auto",
      footer: "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      closeButton:
        "absolute right-4 top-4 rounded-sm opacity-70 hover:opacity-100",
    },
  },

  effects: {
    gradients: {
      primary: "linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)",
      secondary: "linear-gradient(135deg, #94a3b8 0%, #64748b 100%)",
      accent: "linear-gradient(135deg, #a78bfa 0%, #8b5cf6 100%)",
    },

    backdropBlur: {
      none: "blur(0)",
      sm: "blur(4px)",
      base: "blur(8px)",
      md: "blur(12px)",
      lg: "blur(16px)",
    },
  },
};
